import * as React from 'react';
import { Box, Typography} from '@mui/material';
import FileUploadPreview, { PreviewFile } from './documents/FileUploadPreview';
import DocumentList  from './documents/DocumentList';
import { Client, DocumentRef, DocumentStatus, Patient } from '../../../models/core';
import { API } from '../../../utils/Api';
import { API as ZiphyAPI } from '../../../utils/ZiphyAPI';
import { useQuery } from '@tanstack/react-query';
import { LoadingOverlay } from '../../LoadingOverlay';
import useSnackbar from '../../../hooks/useSnackbar';
import AlertSnack from '../../ErrorSnack';
import { AppointmentEncounters, fetchAppointmentsWithEncounters } from '../../../utils/fetchUtils';
import EncounterSelectDialog from '../../dialogs/EncounterSelectDialog';
import { useDocumentsListQuery } from '../../../queries/useDocumentListQuery';

interface PatientDocumentProps {
    open?:boolean;
    patient:Patient;
    client:Client;
}

function useAppointmentQuery(client:Client, patient:Patient) {
  return useQuery({
    queryKey:['appointments.list_encounters', client._id, patient._id, patient.zcmId],
    queryFn: async () => {
        if(!patient || !patient.zcmId || patient.zcmId?.trim() == '') {
            return { appointments:[], encounters:[] }
        }
        return await fetchAppointmentsWithEncounters(patient.zcmId || '', client.practiceId);
    },
    select:(data) => data || { appointments:[], encounters:[] } as AppointmentEncounters,
    enabled:true,
    staleTime:240_000
  });
}

async function calculateSha256Base64(file: File): Promise<string> {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    let binary = '';
    hashArray.forEach(byte => {
        binary += String.fromCharCode(byte);
    });
    return btoa(binary);
}

const PatientDocumentView = ({ open, client, patient }: PatientDocumentProps): JSX.Element => {
    const snackBar = useSnackbar();

    const pad = (n: number) => n.toString().padStart(2, '0');

    const appointmentsQuery = useAppointmentQuery(client, patient);
    const documentQuery = useDocumentsListQuery(patient.clientId, patient._id);
    const documents = React.useMemo(() => { return documentQuery?.data?.items || []}, [documentQuery]);
    const [ isUploading, setIsUploading ] = React.useState<boolean>(false);

    const encounters = React.useMemo(() => { return appointmentsQuery.data || { appointments:[], encounters:[] } }, [appointmentsQuery])
    const [encounterDialogOpen, setEncounterDialogOpen] = React.useState<boolean>(false);
    const [uploadDocument, setUploadDocument] = React.useState<DocumentRef>();

    React.useEffect(() => { setIsUploading(false);}, [patient])
    
    const onUpload = React.useCallback(async (files:PreviewFile[])=> {
        setIsUploading(true);
        const now = new Date();
        const nowStr = now.toISOString();
        const docRefs = files.map((file) => { 
            return  {
                src:file.previewUrl,
                file:file.file,
                doc:{
                    patientId:patient._id,
                    clientId:patient.clientId,
                    details:file.description,
                    emr:[{key:'*', asset:'', status:DocumentStatus.PENDING}],
                    created:nowStr,
                    size:file.file.size,
                    type:file.file.type,
                    documentType:file.documentType,
                    name:`${patient.lastName?.toLocaleLowerCase()}_${patient.firstName?.toLocaleLowerCase()}`
                        + `_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_`
                        + `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
                        + `${file.file.name.substring(file.file.name.lastIndexOf('.'))}`

                } as DocumentRef
            }
        });

        if(docRefs && docRefs?.length != 0) {
            for(const document of docRefs) {
                const checksum = await calculateSha256Base64(document.file);
                const request = await API.createDocument(client, patient._id, checksum, document.doc)
                try {
                    if(request && request.acknowledged && request.insertedId) {

                        if(request.data?.url && request.data?.url?.trim() !== '') {
                            const uploadRes = await fetch(request.data?.url , {
                                method: 'PUT',
                                headers: {
                                    'Content-Type': document.doc.type,
                                    'x-amz-checksum-sha256': checksum,
                                },
                                body: document.file
                            });

                            if(!uploadRes?.ok) {
                                const error = await uploadRes.text();
                                console.error(error);
                                await API.abortDocument(patient.clientId, patient._id, request.insertedId)
                                snackBar.showError(new Error(`Failed to create document: ${document.doc.name}`));
                                continue;
                            }
                        }
                        await API.finalizeDocument(patient.clientId, patient._id, request.insertedId)
                    }
                } catch(error) {
                    snackBar.showError(new Error(`Failed to create document: ${document.doc.name}`));
                    console.error(error);
                    try { await API.abortDocument(patient.clientId, patient._id, request.insertedId) } catch(err) { console.error(error) }
                }
            }
            await documentQuery.refetch();
        }
        setIsUploading(false);
    },[patient, documentQuery, snackBar, client])

    const syncDocumentToZiphy = async (doc:DocumentRef, encounter:number) => {
        console.log('Disabled');
        //Jessie_Sample_19800522_unspecified_referrer_20250604_123143_13264.pdf
        /*const now = new Date();
        const dob = DateTime.fromISO(patient.dob||'');
        const fileName = `${patient.firstName?.toLocaleLowerCase()}_${patient.lastName?.toLocaleLowerCase()}`
                        + `_${dob.year}${pad(dob.month)}${pad(dob.day)}`
                        + `_${doc.documentType}`
                        + `_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`
                        + `_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
                        + `${doc.name.substring(doc.name.lastIndexOf('.'))}`;
        const req = await ZiphyAPI.getDocumentUploadLink(doc.type, fileName, encounter);
        console.log(req);
        if(!req) {
            //  TODO
        }
        const urlList = req as {urls:{url:string, filename:string, fields:{[key:string]:string}}[]};
        console.log(urlList);
        if(!urlList || urlList.urls?.length  == 0) {
            //  TODO
        }
        const targetUrl = urlList?.urls[0];
        if(!targetUrl) {
            //  TODO
        }

        
        const fields =  Object.entries(targetUrl.fields || {});
        const headersObj = Object.fromEntries(fields);
        const uploadRes = await fetch(docRequest.data?.url , {
            method: 'PUT',
            headers: headersObj,
            body:
        });*/
    }

    return <Box>
        <FileUploadPreview onUpload={onUpload} usePublicAPI={false}/>
        <Box p={2}>
        <Typography variant="h6">User Documents</Typography>
            <DocumentList
                usePublicAPI={false}
                files={documents}
                onUploadToEHR={
                    /*!patient?.zcmId || patient?.zcmId?.trim() == ''
                    ? undefined
                    : async (file) => {
                        setUploadDocument(file);
                        setEncounterDialogOpen(true);
                        await appointmentsQuery.refetch();
                    }*/
                   () => {console.error('EHR Is Currently Disabled')}
                }
                onDownload={async (file) => {
                    try {
                        const request = await API.createDocumentLink(file);
                        if (!request || !request.acknowledged || !request.data?.url) {
                            snackBar.showError(new Error(`Failed to retrieve download link for: ${file.name}`));
                            return;
                        }

                        const response = await fetch(request.data.url);
                        if (!response.ok) {
                            const throwError = new Error(`Failed to fetch file: ${response.statusText}`);
                            snackBar.showError(throwError);
                            throw throwError;
                        }

                        const blob = await response.blob();
                        const fileURL = window.URL.createObjectURL(blob);

                        const alink = document.createElement("a");
                        alink.href = fileURL;
                        alink.download = file.name;
                        document.body.appendChild(alink); // sometimes needed for Firefox
                        alink.click();
                        document.body.removeChild(alink);

                        window.URL.revokeObjectURL(fileURL);
                    } catch (error) {
                        console.log(error);
                    }
                }}
            />
        </Box>
        <EncounterSelectDialog open={encounterDialogOpen} appointments={encounters} 
            onClose={() => setEncounterDialogOpen(false)} 
                title={'Select Encounter'}
                body={
                    <Box>
                        <Typography variant='body1'>Select the encounter this document is associated with.</Typography>
                        <Typography variant='body1'>Once associated this action cannot be undone.</Typography>
                    </Box>
                }
                zcmId={Number.parseInt(patient.zcmId||'')}
                onConfirm={async (apt) => {
                    if(uploadDocument) {
                        await syncDocumentToZiphy(uploadDocument, apt)
                    }
                }
            }/>
        <LoadingOverlay open={isUploading} />
        <AlertSnack {...snackBar}/>
    </Box>
}

export default PatientDocumentView;